import { useState } from 'react';
import { Icon } from '../../ui/index.js';
import { cn } from '../../lib/cn.js';

interface Props {
  /** Current rating, 1..5. 0 / null / undefined → unrated. */
  value: number | null | undefined;
  /** When set, stars are clickable. Omit for viewer-mode. */
  onChange?: (next: number) => void | Promise<void>;
  /** Visual size; matches Tailwind's icon scale tokens. */
  size?: 'sm' | 'md' | 'lg';
  /** When true, clicking the same star clears the rating (set to 0). */
  allowClear?: boolean;
  className?: string;
  ariaLabel?: string;
}

const SIZE_CLASSES: Record<NonNullable<Props['size']>, string> = {
  sm: 'text-base',
  md: 'text-xl',
  lg: 'text-2xl',
};

// 5 clickable stars. Hover preview only when editable. Re-clicking the same
// star clears the rating to 0 if allowClear is set (default behaviour) — same
// affordance as Apple Photos.
export function StarRating({
  value,
  onChange,
  size = 'md',
  allowClear = true,
  className,
  ariaLabel,
}: Props) {
  const editable = Boolean(onChange);
  const [hover, setHover] = useState<number>(0);
  const current = value ?? 0;
  const display = editable && hover > 0 ? hover : current;

  function handleClick(n: number) {
    if (!onChange) return;
    const next = allowClear && current === n ? 0 : n;
    void onChange(next);
  }

  return (
    <div
      role={editable ? 'radiogroup' : 'img'}
      aria-label={ariaLabel ?? (editable ? 'Valutazione' : `${current} stelle`)}
      className={cn('inline-flex items-center gap-0.5', className)}
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= display;
        return (
          <button
            key={n}
            type="button"
            disabled={!editable}
            onClick={() => handleClick(n)}
            onMouseEnter={() => editable && setHover(n)}
            aria-checked={editable ? n === current : undefined}
            role={editable ? 'radio' : undefined}
            aria-label={`${n} ${n === 1 ? 'stella' : 'stelle'}`}
            className={cn(
              'rounded p-0.5 transition-colors',
              editable &&
                'hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan/60',
              !editable && 'cursor-default',
            )}
          >
            <Icon
              name="star"
              filled={filled}
              className={cn(
                SIZE_CLASSES[size],
                filled ? 'text-warning' : 'text-white/30',
                editable && 'transition-transform group-hover:scale-110',
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
